import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { acceptLanguageHeader } from '@v2/lib/locale';

const api = axios.create({
    baseURL: '/api/v2',
    withCredentials: true,
    headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

function readCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function applyCsrfHeaders(config: InternalAxiosRequestConfig): void {
    const meta = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (meta) {
        config.headers['X-CSRF-TOKEN'] = meta;
    }

    // Sanctum stateful: cookie XSRF-TOKEN acompanha a sessão; meta pode ficar stale.
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) {
        config.headers['X-XSRF-TOKEN'] = xsrf;
    }
}

api.interceptors.request.use((config) => {
    config.headers['Accept-Language'] = acceptLanguageHeader();
    applyCsrfHeaders(config);
    return config;
});

let csrfRefresh: Promise<void> | null = null;

async function refreshCsrfCookie(): Promise<void> {
    if (!csrfRefresh) {
        csrfRefresh = axios
            .get('/sanctum/csrf-cookie', { withCredentials: true })
            .then(() => undefined)
            .finally(() => {
                csrfRefresh = null;
            });
    }

    await csrfRefresh;
}

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const config = error.config as (InternalAxiosRequestConfig & { _csrfRetried?: boolean }) | undefined;
        if (error.response?.status === 419 && config && !config._csrfRetried) {
            config._csrfRetried = true;
            try {
                await refreshCsrfCookie();
                applyCsrfHeaders(config);
                return api.request(config);
            } catch {
                // fall through to original error
            }
        }

        return Promise.reject(error);
    },
);

export default api;
